import { useCallback, useRef, useState, type MutableRefObject } from "react";
import { toast } from "react-toastify";

import { woocommerceStoreService } from "../services/woocommerceStoreService";
import type { WooOrder, WooPixPaymentDetails, WooStoreConfig } from "../types";
import { shouldUseEmbeddedMarketplacePayment } from "../utils/embeddedPayment";
import { isPaidOrder } from "../utils/orderHelpers";
import {
  buildPaymentLoginRedirectUrl,
  openPaymentPopup,
  openPaymentPopupWindow,
} from "../utils/openIdLogin";
import {
  extractPixPaymentFromOrder,
  hasActivePixQrCode,
} from "../utils/pixPaymentHelpers";

const PAYMENT_POLL_INTERVAL_MS = 5000;
const MAX_POLLS_WITHOUT_POPUP = 12;
const MAX_MONITORING_MS = 15 * 60 * 1000;

interface PixPaymentSession {
  order: WooOrder;
  details: WooPixPaymentDetails;
}

export interface EmbeddedPaymentSession {
  url: string;
  storeName: string;
}

type PollResult = "paid" | "pix" | "unpaid" | "timeout" | "cancelled";

interface UseWooCommercePaymentMonitorOptions {
  store: WooStoreConfig | null;
  resolveStore?: (order: WooOrder) => WooStoreConfig | null;
  onOrdersRefresh: (customerId?: number) => Promise<unknown>;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isPopupClosed(popup: Window | null): boolean {
  return !popup || popup.closed;
}

/** Monitora popup, iframe embarcado (mobile) ou QR PIX inline + polling até confirmação. */
export function useWooCommercePaymentMonitor({
  store,
  resolveStore,
  onOrdersRefresh,
}: UseWooCommercePaymentMonitorOptions) {
  const [monitoringOrderId, setMonitoringOrderId] = useState<number | null>(
    null,
  );
  const [successOrder, setSuccessOrder] = useState<WooOrder | null>(null);
  const [pixPayment, setPixPayment] = useState<PixPaymentSession | null>(null);
  const [embeddedPayment, setEmbeddedPayment] =
    useState<EmbeddedPaymentSession | null>(null);
  const monitorCancelRef = useRef(false);
  const embeddedPaymentOpenRef = useRef(false);

  const stopMonitoring = useCallback(() => {
    monitorCancelRef.current = true;
  }, []);

  const clearEmbeddedPayment = useCallback(() => {
    embeddedPaymentOpenRef.current = false;
    setEmbeddedPayment(null);
  }, []);

  const closeEmbeddedPayment = useCallback(() => {
    clearEmbeddedPayment();
  }, [clearEmbeddedPayment]);

  const closePixPayment = useCallback(() => {
    stopMonitoring();
    clearEmbeddedPayment();
    setPixPayment(null);
    setMonitoringOrderId(null);
    toast.info(
      "Pagamento PIX pendente. Você pode retomar em Meus pedidos.",
    );
  }, [stopMonitoring, clearEmbeddedPayment]);

  const pollUntilResolved = useCallback(
    async (
      activeStore: WooStoreConfig,
      order: WooOrder,
      options?: {
        popup?: Window | null;
        embeddedSessionOpenRef?: MutableRefObject<boolean>;
        switchToPixOnExternalClose?: boolean;
      },
    ): Promise<PollResult> => {
      const startedAt = Date.now();
      let pollsWithoutExternalSession = 0;
      const popup = options?.popup ?? null;
      const hasPopupRef = popup !== null;
      const embeddedRef = options?.embeddedSessionOpenRef;
      monitorCancelRef.current = false;

      const isEmbeddedSessionOpen = () =>
        embeddedRef !== undefined && embeddedRef.current;

      const handleExternalSessionClosed = async (): Promise<PollResult> => {
        const updated = await woocommerceStoreService.getOrder(
          activeStore,
          order.id,
        );
        await onOrdersRefresh(order.customerId);

        if (isPaidOrder(updated)) {
          return "paid";
        }

        if (
          options?.switchToPixOnExternalClose &&
          hasActivePixQrCode(updated)
        ) {
          const details = extractPixPaymentFromOrder(updated);
          if (details) {
            setPixPayment({ order: updated, details });
            return "pix";
          }
        }

        return "unpaid";
      };

      while (!monitorCancelRef.current) {
        if (Date.now() - startedAt >= MAX_MONITORING_MS) {
          return "timeout";
        }

        if (hasPopupRef && isPopupClosed(popup)) {
          return handleExternalSessionClosed();
        }

        if (embeddedRef !== undefined && !embeddedRef.current) {
          return handleExternalSessionClosed();
        }

        const updated = await woocommerceStoreService.getOrder(
          activeStore,
          order.id,
        );
        await onOrdersRefresh(order.customerId);

        if (isPaidOrder(updated)) {
          return "paid";
        }

        if (!hasPopupRef && !isEmbeddedSessionOpen() && hasActivePixQrCode(updated)) {
          const details = extractPixPaymentFromOrder(updated);
          if (details) {
            setPixPayment((current) =>
              current ?? { order: updated, details },
            );
          }
        }

        if (
          !hasPopupRef &&
          !isEmbeddedSessionOpen() &&
          !hasActivePixQrCode(updated)
        ) {
          pollsWithoutExternalSession += 1;
          if (pollsWithoutExternalSession >= MAX_POLLS_WITHOUT_POPUP) {
            return "timeout";
          }
        }

        await sleep(PAYMENT_POLL_INTERVAL_MS);
      }

      return "cancelled";
    },
    [onOrdersRefresh],
  );

  const payOrder = useCallback(
    async (order: WooOrder) => {
      const activeStore = resolveStore?.(order) ?? store;
      if (!activeStore) return;

      setMonitoringOrderId(order.id);
      monitorCancelRef.current = false;

      const finishUnpaid = (message: string) => {
        clearEmbeddedPayment();
        toast.info(message);
        setMonitoringOrderId(null);
      };

      const markPaid = async () => {
        const paid = await woocommerceStoreService.getOrder(
          activeStore,
          order.id,
        );
        clearEmbeddedPayment();
        setPixPayment(null);
        setSuccessOrder(paid);
        setMonitoringOrderId(null);
      };

      try {
        const freshOrder = await woocommerceStoreService.getOrder(
          activeStore,
          order.id,
        );

        if (hasActivePixQrCode(freshOrder)) {
          const details = extractPixPaymentFromOrder(freshOrder);
          if (!details) {
            toast.error("Não foi possível carregar o QR Code PIX.");
            setMonitoringOrderId(null);
            return;
          }

          setPixPayment({ order: freshOrder, details });
          const result = await pollUntilResolved(activeStore, freshOrder);
          if (result === "paid") {
            await markPaid();
          }
          return;
        }

        // Mobile / WebView (fluxmedportable): tela interna em vez de popup
        const useEmbeddedPayment = shouldUseEmbeddedMarketplacePayment();

        if (useEmbeddedPayment) {
          const loginRedirectUrl = buildPaymentLoginRedirectUrl(
            activeStore,
            freshOrder.paymentUrl,
          );
          embeddedPaymentOpenRef.current = true;
          setEmbeddedPayment({
            url: loginRedirectUrl,
            storeName: activeStore.name,
          });
        } else {
          const popup = openPaymentPopupWindow(
            activeStore,
            freshOrder.paymentUrl,
          );
          if (!popup) {
            openPaymentPopup(activeStore, freshOrder.paymentUrl);
          }

          const popupResult = await pollUntilResolved(activeStore, freshOrder, {
            popup,
            switchToPixOnExternalClose: true,
          });

          if (popupResult === "paid") {
            await markPaid();
            return;
          }

          if (popupResult === "pix") {
            const pixResult = await pollUntilResolved(activeStore, freshOrder);
            if (pixResult === "paid") {
              await markPaid();
            }
            return;
          }

          if (popupResult === "timeout") {
            finishUnpaid(
              "Pagamento não concluído. Você pode tentar novamente em Meus pedidos.",
            );
            return;
          }

          finishUnpaid(
            "Pagamento não concluído. Você pode tentar novamente em Meus pedidos.",
          );
          return;
        }

        const embeddedResult = await pollUntilResolved(activeStore, freshOrder, {
          embeddedSessionOpenRef: embeddedPaymentOpenRef,
          switchToPixOnExternalClose: true,
        });

        clearEmbeddedPayment();

        if (embeddedResult === "paid") {
          await markPaid();
          return;
        }

        if (embeddedResult === "pix") {
          const pixResult = await pollUntilResolved(activeStore, freshOrder);
          if (pixResult === "paid") {
            await markPaid();
          }
          return;
        }

        if (embeddedResult === "timeout") {
          finishUnpaid(
            "Pagamento não concluído. Você pode tentar novamente em Meus pedidos.",
          );
          return;
        }

        finishUnpaid(
          "Pagamento não concluído. Você pode retomar em Meus pedidos.",
        );
      } catch {
        clearEmbeddedPayment();
        toast.error("Não foi possível verificar o pagamento.");
        setPixPayment(null);
        setMonitoringOrderId(null);
      }
    },
    [store, resolveStore, pollUntilResolved, clearEmbeddedPayment],
  );

  const clearSuccessOrder = useCallback(() => {
    setSuccessOrder(null);
  }, []);

  return {
    payOrder,
    monitoringOrderId,
    successOrder,
    clearSuccessOrder,
    pixPayment,
    closePixPayment,
    embeddedPayment,
    closeEmbeddedPayment,
    isMonitoring: monitoringOrderId !== null,
  };
};
